export type VendorRef = { id: number; name: string; is_active: boolean } | null;

export type AttachmentRow = {
  id: number;
  location: {
    id: number;
    name: string;
    is_active: boolean;
    vendor_id: number | null;
    vendor: VendorRef;
  } | null;
  menu: {
    id: number;
    name: string;
    is_active: boolean;
    vendor_id: number | null;
    vendor: VendorRef;
  } | null;
};

// One half of a mismatch card: the vendor is the headline, the record it owns
// is the fine print underneath.
const OwnerHalf = ({
  role,
  chip,
  recordName,
  recordId,
  vendorId,
  vendor,
}: {
  role: string;
  chip: { label: string; className: string };
  recordName: string;
  recordId: number;
  vendorId: number | null;
  vendor: VendorRef;
}) => (
  <div className="min-w-0">
    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
      {role}
    </div>
    <div className="truncate text-sm font-semibold text-gray-900">
      {vendorId == null ? (
        <span className="font-normal text-gray-500">no vendor assigned</span>
      ) : (
        <>
          <span className="font-mono font-normal text-gray-400">#{vendorId}</span>{" "}
          {vendor?.name?.trim() || "?"}
          {vendor && !vendor.is_active && (
            <span className="ml-1.5 rounded bg-red-100 px-1 py-px align-middle text-[10px] font-semibold text-red-700">
              INACTIVE
            </span>
          )}
        </>
      )}
    </div>
    <div className="mt-0.5 truncate text-xs text-gray-500">
      <span className={`mr-1 rounded px-1 py-px text-[10px] font-semibold ${chip.className}`}>
        {chip.label}
      </span>
      "{recordName}" <span className="text-gray-400">#{recordId}</span>
    </div>
  </div>
);

// Menus attached to a location owned by a different vendor. Sometimes this is
// deliberate (an org sharing one menu across its house vendors), but it's also
// exactly how live config ends up owned by retired vendors — the menu-clone
// form silently inherits the source menu's vendor. Surfaced here so someone
// reviews each one instead of finding out during a cleanup.
export const EventConfigDiscrepanciesSection = ({
  rows,
  loading,
  error,
}: {
  rows: AttachmentRow[];
  loading: boolean;
  error: string | null;
}) => {
  const mismatches = rows.filter(
    (r) => r.location && r.menu && r.menu.vendor_id !== r.location.vendor_id
  );

  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6 break-inside-avoid">
      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-3">
        <h2 className="text-base font-semibold text-gray-900">
          Menu Ownership Check
          <span className="ml-2 text-xs font-normal text-gray-500">
            menus attached to a location owned by a different vendor
          </span>
        </h2>
        {!loading && !error && mismatches.length > 0 && (
          <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
            {mismatches.length} to review
          </span>
        )}
      </div>

      {loading && (
        <div className="px-6 py-4 text-sm text-gray-500">Checking menu ownership…</div>
      )}
      {error && <div className="px-6 py-4 text-sm text-red-700">{error}</div>}

      {!loading && !error && mismatches.length === 0 && (
        <div className="px-6 py-4 text-sm text-green-700">
          ✓ Every menu at this event is owned by the vendor of the location it serves.
        </div>
      )}

      {!loading && !error && mismatches.length > 0 && (
        <div className="divide-y divide-gray-100">
          {mismatches.map((r) => {
            const menu = r.menu!;
            const loc = r.location!;
            return (
              <div
                key={r.id}
                className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 bg-amber-50/60 px-6 py-3"
              >
                <OwnerHalf
                  role="Menu owned by"
                  chip={{ label: "MENU", className: "bg-purple-100 text-purple-800" }}
                  recordName={menu.name}
                  recordId={menu.id}
                  vendorId={menu.vendor_id}
                  vendor={menu.vendor}
                />
                <span
                  className="text-lg font-bold text-amber-600"
                  title="The menu's owner and the location's owner are different vendors"
                >
                  ≠
                </span>
                <OwnerHalf
                  role="Location owned by"
                  chip={{ label: "LOCATION", className: "bg-blue-100 text-blue-800" }}
                  recordName={loc.name}
                  recordId={loc.id}
                  vendorId={loc.vendor_id}
                  vendor={loc.vendor}
                />
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
